import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  abandonBundleUploadApiV1BundleSplitterUploadsUploadIdAbandonPost,
  classifyFilesApiV1BundleSplitterClassifyPost,
  classifyFilesByRequestApiV1BundleSplitterClassifyByRequestPost,
  getBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost,
  heartbeatBundleUploadApiV1BundleSplitterUploadsUploadIdHeartbeatPost,
  saveClassifiedFilesApiV1BundleSplitterCaseCaseNumberSavePost,
} from "@/api/generated/sdk.gen";
import type {
  BodyClassifyFilesApiV1BundleSplitterClassifyPost,
  BodyGetBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost,
  BundleSplitterSasTokenResponse,
  ClassifyFilesRequest,
  SaveFilesRequest,
  SaveFilesResponse,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

import { buildApiErrorMessage } from "@utils/notification";

/**
 * Hook for getting a SAS token for uploading files to bundle splitter
 */
export const useGetBundleSplitterSasToken = () => {
  return useMutation({
    mutationFn: async (
      data: BodyGetBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost
    ): Promise<BundleSplitterSasTokenResponse> => {
      const response =
        await getBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost({
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to get SAS token", response.error)
        );
      }

      if (!response.data) {
        throw new Error("No data returned from SAS token API");
      }

      return response.data;
    },
    onError: (error) => {
      console.error("Error getting SAS token:", error);
    },
  });
};

/**
 * How long a failed upload waits for a `securitypolicyviolation` that hasn't
 * arrived yet before deciding it was a network failure rather than a policy
 * block. Nothing orders that event against the XHR `error` event, so without a
 * grace period a violation queued second would be missed entirely.
 *
 * Only a failing upload waits, and only when no violation has been seen — so
 * this costs nothing on success and nothing on an already-identified block.
 */
export const CSP_VIOLATION_GRACE_MS = 50;

/**
 * Upload file directly to Azure blob storage using SAS URL
 */
export const uploadFileToAzureBlob = async (
  file: File,
  sasUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    let host = "blob storage";
    try {
      host = new URL(sasUrl).host;
    } catch {
      // Malformed SAS URL — fall back to the generic label.
    }

    // Tracked unconditionally, not just when a progress callback is supplied —
    // it goes in the failure message, where knowing whether anything reached
    // the wire is the difference between "never connected" and "died midway".
    let bytesSent = 0;

    xhr.upload.addEventListener("progress", (event) => {
      bytesSent = event.loaded;
      if (onProgress && event.lengthComputable) {
        onProgress((event.loaded / event.total) * 100);
      }
    });

    // The browser fires this on the document when a Content-Security-Policy
    // blocks a request, naming the URI it refused. That is a DEFINITIVE signal,
    // unlike inferring it from the byte count: a refused connection, a DNS
    // failure, an offline client and a TLS error all also reach the `error`
    // handler having sent nothing, and calling those a configuration problem
    // would be worse than the catch-all this replaces — it would tell someone
    // with no wifi to contact support.
    //
    // Two conditions, both load-bearing:
    //
    //   disposition === "enforce"  A `Content-Security-Policy-Report-Only`
    //     policy fires this same event WITHOUT blocking anything. We intend to
    //     roll future policies out report-only first (MDRS-638), so during such
    //     a rollout a report-only violation naming this host would sit
    //     alongside an unrelated network failure and wrongly claim the upload
    //     was blocked. Only an enforcing policy actually stops a request.
    //
    //   host match on the parsed URI  `blockedURI` is frequently truncated to
    //     the origin for a cross-origin request, so compare hosts rather than
    //     substring-matching the whole string — a different blocked origin that
    //     merely contains this host would otherwise count.
    //
    //   connect-src  this listener is on the document, so it sees EVERY
    //     violation on the page. The policy names the blob host in three
    //     directives, so a blocked preview image from the same host fires here
    //     too — and would mark an unrelated network failure as a policy block.
    //     XHR is governed by connect-src specifically; nothing else blocking
    //     that host says anything about this upload.
    let blockedByPolicy = false;
    const onViolation = (event: Event) => {
      const violation = event as SecurityPolicyViolationEvent;
      if (violation.disposition !== "enforce") return;

      // `violatedDirective` carries the directive plus its source list on older
      // browsers; `effectiveDirective` is just the name. Prefix-matching covers
      // both, and no other directive begins with "connect-src".
      const directive =
        violation.effectiveDirective || violation.violatedDirective || "";
      if (!directive.startsWith("connect-src")) return;

      const blockedUri = violation.blockedURI;
      if (typeof blockedUri !== "string") return;

      let blockedHost = blockedUri;
      try {
        blockedHost = new URL(blockedUri).host;
      } catch {
        // Not an absolute URI (e.g. the literal "self"/"inline" keywords, or an
        // origin already stripped of its scheme) — compare what we were given.
      }
      if (blockedHost === host) blockedByPolicy = true;
    };
    document.addEventListener("securitypolicyviolation", onViolation);
    const stopListening = () =>
      document.removeEventListener("securitypolicyviolation", onViolation);

    xhr.addEventListener("load", () => {
      stopListening();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `Failed to upload file to Azure blob storage: ${xhr.status} ${xhr.statusText} - ${xhr.responseText}`
          )
        );
      }
    });

    // XHR reports every pre-response failure the same way: an `error` event with
    // status 0 and no detail. That lumps together a request the browser REFUSED
    // TO SEND and a connection that genuinely dropped. Reporting both as "check
    // your connection" is what sent a CSP misconfiguration to be chased as a
    // network problem for a day before anyone read the response headers.
    //
    // Only a confirmed policy violation earns the configuration message. Anything
    // else stays on the connectivity wording, because that is both the common
    // case and the one the user can actually act on.
    const settle = () => {
      stopListening();
      reject(
        new Error(
          blockedByPolicy
            ? `Upload to ${host} was blocked by the browser's security policy. The Content-Security-Policy served to this page does not allow requests to that host.`
            : `Network error occurred while uploading file to Azure blob storage (${host}, after ${bytesSent} bytes)`
        )
      );
    };

    xhr.addEventListener("error", () => {
      // Nothing orders `securitypolicyviolation` before the XHR `error` — both
      // are queued tasks, and which lands first is left to the implementation.
      // Tearing the listener down here would mean a violation arriving second
      // is never seen, silently degrading to the "check your connection"
      // message this change exists to stop.
      //
      // So when no violation has been seen yet, give one a bounded chance to
      // arrive before classifying. Only a failing upload pays the delay, and
      // only on the path that hasn't already been identified.
      if (blockedByPolicy) {
        settle();
        return;
      }
      setTimeout(settle, CSP_VIOLATION_GRACE_MS);
    });

    xhr.addEventListener("abort", () => {
      stopListening();
      reject(new Error("File upload was aborted"));
    });

    xhr.open("PUT", sasUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader("Content-Type", file.type || "application/pdf");

    xhr.send(file);
  });
};

/**
 * Hook for uploading files directly to Azure blob storage
 */
export const useUploadToAzureBlob = () => {
  return useMutation({
    mutationFn: async ({
      file,
      sasUrl,
      onProgress,
    }: {
      file: File;
      sasUrl: string;
      onProgress?: (progress: number) => void;
    }) => {
      await uploadFileToAzureBlob(file, sasUrl, onProgress);
      return { success: true };
    },
    onError: (error) => {
      console.error("Error uploading to Azure blob:", error);
    },
  });
};

/**
 * How often an in-flight bundle upload tells the server it's still running
 * (MDRS-508). The server stops counting an upload a few beats after the last
 * one, so an upload that dies without a word — network dropped, tab closed —
 * releases the doctor's Finalise within minutes instead of an hour.
 */
export const BUNDLE_UPLOAD_HEARTBEAT_MS = 60_000;

const heartbeatBundleUpload = async (uploadId: string) => {
  try {
    await heartbeatBundleUploadApiV1BundleSplitterUploadsUploadIdHeartbeatPost({
      client: apiClient,
      path: { upload_id: uploadId },
    });
  } catch {
    // Ignored — a missed beat is retried on the next tick, and the server's
    // TTL spans several beats.
  }
};

/**
 * Best-effort: tell the server a bundle upload is over after it failed, so the
 * case stops showing as receiving documents and the doctor can finalise again
 * (MDRS-508). If this fails too (e.g. offline), the heartbeat has stopped, so
 * the server stops counting the upload shortly after anyway.
 */
const abandonBundleUpload = async (uploadId: string) => {
  try {
    await abandonBundleUploadApiV1BundleSplitterUploadsUploadIdAbandonPost({
      client: apiClient,
      path: { upload_id: uploadId },
    });
  } catch {
    // Ignored — see above. The upload's own error is what the user needs.
  }
};

/**
 * Combined hook that handles the complete upload flow:
 * 1. Get SAS token
 * 2. Upload file to Azure blob storage
 * 3. Return the blob path for further processing
 */
export const useBundleSplitterUpload = () => {
  const getSasToken = useGetBundleSplitterSasToken();
  const uploadToBlob = useUploadToAzureBlob();

  const uploadFile = async (
    file: File,
    caseNumber: string,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    // Step 1: Get SAS token
    const sasTokenResponse = await getSasToken.mutateAsync({
      case_number: caseNumber,
      filename: file.name,
    });

    // Step 2: Upload file to Azure blob storage. Issuing the SAS marked the
    // case as receiving this upload (MDRS-508): keep it alive while the file
    // is on its way; classify clears it on success, so only a failed upload
    // has to clear it here. The server refuses the SAS when it can't record
    // the upload, so upload_id is always set; a missing one is just skipped.
    const uploadId = sasTokenResponse.upload_id ?? null;
    const heartbeat = uploadId
      ? setInterval(
          () => void heartbeatBundleUpload(uploadId),
          BUNDLE_UPLOAD_HEARTBEAT_MS
        )
      : undefined;
    try {
      await uploadToBlob.mutateAsync({
        file,
        sasUrl: sasTokenResponse.sas_url,
        onProgress,
      });
    } catch (error) {
      if (uploadId) await abandonBundleUpload(uploadId);
      throw error;
    } finally {
      clearInterval(heartbeat);
    }

    // Step 3: Return the blob path for classification
    return sasTokenResponse.blob_path;
  };

  return {
    uploadFile,
    isLoading: getSasToken.isPending || uploadToBlob.isPending,
    error: getSasToken.error || uploadToBlob.error,
    isSuccess: getSasToken.isSuccess && uploadToBlob.isSuccess,
    reset: () => {
      getSasToken.reset();
      uploadToBlob.reset();
    },
  };
};

/**
 * Hook for submitting files for classification via direct file upload
 */
export const useClassifyFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BodyClassifyFilesApiV1BundleSplitterClassifyPost) => {
      const response = await classifyFilesApiV1BundleSplitterClassifyPost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to classify files", response.error)
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Optionally invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      // MDRS-550: the bundle pop-up spots a file already uploaded to the case
      // from the task list, so the new task has to show up there.
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.BUNDLE_SPLITTER, CLIENT_KEYS.TASKS],
      });

      return data;
    },
    onError: (error) => {
      console.error("Error classifying files:", error);
    },
  });
};

/**
 * Hook for classifying files using file references instead of direct uploads
 */
export const useClassifyFilesByRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ClassifyFilesRequest) => {
      const response =
        await classifyFilesByRequestApiV1BundleSplitterClassifyByRequestPost({
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to classify files by request", response.error)
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Optionally invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });

      return data;
    },
    onError: (error) => {
      console.error("Error classifying files by request:", error);
    },
  });
};

/**
 * Hook for saving classified files from bundle splitter results
 */
export const useSaveClassifiedFiles = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: SaveFilesResponse) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      case_number,
      data,
    }: {
      case_number: string;
      data: SaveFilesRequest;
    }) => {
      const response =
        await saveClassifiedFilesApiV1BundleSplitterCaseCaseNumberSavePost({
          path: { case_number },
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to save classified files", response.error)
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch relevant queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
      // Pick up auto-mapped LOI questions once the background task lands.
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR],
      });

      onSuccess?.(data);
    },
    onError: (error) => {
      console.error("Error saving classified files:", error);
      onError?.(error);
    },
  });
};
